/**
 * Entrada por teclado musical USB/MIDI (Web MIDI API).
 * Tudo aqui degrada com segurança: se o navegador não oferecer suporte,
 * a função retorna null e o resto da aula continua funcionando normalmente.
 */

export interface MidiDeviceInfo {
  id: string;
  name: string;
  manufacturer: string;
}

export type MidiAccessLike = MIDIAccess;

export function isMidiSupported(): boolean {
  if (typeof navigator === 'undefined') return false;
  return typeof navigator.requestMIDIAccess === 'function';
}

export async function requestMidiAccess(): Promise<MidiAccessLike | null> {
  if (typeof navigator.requestMIDIAccess !== 'function') return null;
  try {
    return await navigator.requestMIDIAccess({ sysex: false });
  } catch {
    return null;
  }
}

export function listDevices(access: MidiAccessLike): MidiDeviceInfo[] {
  const devices: MidiDeviceInfo[] = [];
  access.inputs.forEach((input) => {
    devices.push({
      id: input.id,
      name: input.name || 'Dispositivo MIDI',
      manufacturer: input.manufacturer || '',
    });
  });
  return devices;
}

/** Extrai { midi, velocity } de uma mensagem MIDI Note On, ou null. */
export function parseNoteOn(data: Uint8Array): { midi: number; velocity: number } | null {
  if (data.length < 3) return null;
  const status = data[0] & 0xf0;
  const note = data[1];
  const velocity = data[2];
  if (status !== 0x90 || velocity === 0) return null;
  return { midi: note, velocity };
}

/**
 * Extrai o número MIDI de uma mensagem Note Off, ou null.
 * Cobre as duas formas previstas pela especificação: status 0x80 (Note Off) e
 * status 0x90 (Note On) com velocidade 0 — muitos teclados usam a segunda.
 * É essa mensagem que permite medir até quando o aluno segurou a nota.
 */
export function parseNoteOff(data: Uint8Array): number | null {
  if (data.length < 3) return null;
  const status = data[0] & 0xf0;
  const note = data[1];
  const velocity = data[2];
  if (status === 0x80) return note;
  if (status === 0x90 && velocity === 0) return note;
  return null;
}